// « Comparer les modèles » (owner, 2026-10-23) — /admin/ai-compare, admin only.
//
// Replays real past cases of one AI task on the « ancien » model (what runs
// today) and a « nouveau » one, side by side: output, cost, time, the WhatsApp
// route (Haiku / Sonnet / escalade), red flags, and the owner's verdict. The
// estimate is shown BEFORE « Lancer »; a run never spends past its cap (the
// worker stops it). Linked from Settings › Fournisseurs IA and Rapports › Coûts IA.
//
// Data: worker routes/aiCompare.ts via lib/aiProvidersApi.ts. Night mode: tokens
// only (dashboard.night.guard.test.ts scans this file). Phones: one column.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import { aiFeatureLabel } from '@/lib/aiFeatures';
import { aiProvidersApi, type CompareCase, type CompareHop, type CompareRun, type CompareTask, type FaceBox } from '@/lib/aiProvidersApi';
import {
  boxToRect, clampCases, faceMeta, fmtUsd, hopKey, hopLabel, nextVerdict, parseAmount, parseHopKey,
  routeBadge, runIsLive, runProgress, runTotals, tagLabel, MIXED,
} from '@/lib/aiProvidersUi';
import { useAiProvidersState, inputCls, Bar } from './settings/AiProvidersSections';

const labelCls = 'block text-[11.5px] font-medium text-adm-muted mb-1';
const cardBase = 'bg-adm-surface rounded-xl border p-4 sm:p-5 min-w-0';
const card = `${cardBase} border-adm-line`;
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
// Box colours drawn ON the photo — series hues that read the same in both modes.
const OLD_STROKE = '#0EA5E9';
const NEW_STROKE = '#F97316';

const STATUS_LABEL: Record<CompareRun['status'], string> = {
  queued: 'en file', running: 'en cours', done: 'terminé', stopped: 'arrêté', failed: 'échoué',
};
const STATUS_CLS: Record<CompareRun['status'], string> = {
  queued: 'text-adm-muted', running: 'text-status-waiting', done: 'text-status-done', stopped: 'text-adm-muted', failed: 'text-status-stuck',
};

function StatusChip({ status }: { status: CompareRun['status'] }) {
  const { t } = useT();
  return <span data-run-status={status} className={`text-[11px] font-bold uppercase tracking-wide ${STATUS_CLS[status]}`}>{t(STATUS_LABEL[status])}</span>;
}

// ─── the form ────────────────────────────────────────────────────────────────

function hopOptions(task: CompareTask, models: { anthropic: Array<{ id: string; label: string }>; openrouter: string[] } | undefined, t: (s: string, v?: Record<string, string | number>) => string) {
  const out: Array<{ value: string; label: string }> = [];
  const cur = hopKey(task.current.provider, task.current.model);
  out.push({ value: cur, label: t('{x} — actuel', { x: task.currentLabel }) });
  if (task.current.provider !== 'openrouter' || task.current.model) out.push({ value: hopKey('openrouter', null), label: t('OpenRouter — actuel') });
  for (const m of models?.openrouter ?? []) {
    const k = hopKey('openrouter', m);
    if (k !== cur) out.push({ value: k, label: `OpenRouter — ${m}` });
  }
  if (task.task === 'agent_reply') out.push({ value: hopKey('anthropic', MIXED), label: t('Claude mixte (Haiku/Sonnet)') });
  for (const m of models?.anthropic ?? []) out.push({ value: hopKey('anthropic', m.id), label: m.label });
  return out;
}

function defaultNew(task: CompareTask, models: { anthropic: Array<{ id: string }> } | undefined): string {
  if (task.task === 'agent_reply') return hopKey('anthropic', MIXED);
  const ids = (models?.anthropic ?? []).map((m) => m.id);
  const pick = ids.find((id) => /sonnet/.test(id)) ?? ids[0];
  return pick ? hopKey('anthropic', pick) : hopKey('openrouter', null);
}

const toHop = (k: string): CompareHop => parseHopKey(k) ?? { provider: 'openrouter', model: null };

function NewRunForm({ onStarted }: { onStarted: (id: string) => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const tasksQ = useQuery({ queryKey: ['ai-compare-tasks'], queryFn: aiProvidersApi.compareTasks });
  const stateQ = useAiProvidersState();
  const tasks = tasksQ.data?.tasks ?? [];
  const [taskId, setTaskId] = useState('');
  const task = tasks.find((x) => x.task === taskId) ?? tasks[0];
  const models = stateQ.data?.models;
  const [oldKey, setOldKey] = useState('');
  const [newKey, setNewKey] = useState('');
  const [count, setCount] = useState('10');
  const [cap, setCap] = useState('3');

  // A new task resets both sides: « ancien » = what runs today.
  useEffect(() => {
    if (!task) return;
    setOldKey(hopKey(task.current.provider, task.current.model));
    setNewKey(defaultNew(task, models));
  }, [task?.task, models]); // eslint-disable-line react-hooks/exhaustive-deps

  const n = clampCases(parseInt(count, 10));
  const capUsd = parseAmount(cap) ?? 3;
  const ready = !!task && !!oldKey && !!newKey;
  const est = useQuery({
    queryKey: ['ai-compare-estimate', task?.task, oldKey, newKey, n],
    queryFn: () => aiProvidersApi.compareEstimate(task!.task, toHop(oldKey), toHop(newKey), n),
    enabled: ready && oldKey !== newKey,
    staleTime: 60_000,
  });
  const start = useMutation({
    mutationFn: () => aiProvidersApi.startRun(task!.task, toHop(oldKey), toHop(newKey), n, capUsd),
    onSuccess: ({ run }) => { qc.invalidateQueries({ queryKey: ['ai-compare-runs'] }); onStarted(run.id); },
  });

  if (tasksQ.isLoading) return <div className={card}><p className="text-sm text-adm-muted">{t('Chargement…')}</p></div>;
  if (tasksQ.error) return <div className={card}><p className="text-sm text-status-stuck">{t('Liste des tâches indisponible. Réessayez.')}</p></div>;
  if (!task) return <div className={card}><p className="text-sm text-adm-muted">{t('Aucune tâche comparable pour l’instant.')}</p></div>;

  const opts = hopOptions(task, models, t);
  const e = est.data;
  const over = e ? e.estimateUsd > capUsd : false;
  return (
    <form className={card} data-new-run onSubmit={(ev) => { ev.preventDefault(); if (ready && oldKey !== newKey) start.mutate(); }}>
      <h2 className="text-sm font-semibold text-adm-ink mb-3">{t('Nouvelle comparaison')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_90px_90px] gap-2.5 items-end">
        <label className="min-w-0"><span className={labelCls}>{t('Tâche')}</span>
          <select value={task.task} onChange={(ev) => setTaskId(ev.target.value)} className={inputCls}>
            {tasks.map((x) => <option key={x.task} value={x.task}>{t(aiFeatureLabel(x.task))}</option>)}
          </select></label>
        <label className="min-w-0"><span className={labelCls}>{t('Ancien')}</span>
          <select value={oldKey} onChange={(ev) => setOldKey(ev.target.value)} className={inputCls}>
            {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select></label>
        <label className="min-w-0"><span className={labelCls}>{t('Nouveau')}</span>
          <select value={newKey} onChange={(ev) => setNewKey(ev.target.value)} className={inputCls}>
            {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select></label>
        <div className="grid grid-cols-2 gap-2.5 md:col-span-2 xl:contents">
          <label className="min-w-0"><span className={labelCls}>{t('Cas (max 20)')}</span>
            <input inputMode="numeric" value={count} onChange={(ev) => setCount(ev.target.value.replace(/\D/g, ''))} onBlur={() => setCount(String(n))} className={inputCls} /></label>
          <label className="min-w-0"><span className={labelCls}>{t('Plafond ($)')}</span>
            <input inputMode="decimal" value={cap} onChange={(ev) => setCap(ev.target.value)} className={inputCls} /></label>
        </div>
      </div>
      {task.caseKinds.length > 0 && (
        <p className="mt-2 text-[11.5px] text-adm-muted">
          {t('Cas choisis parmi')} : {task.caseKinds.map((k) => <Chip key={k}>{tagLabel(k, t)}</Chip>)}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3" data-estimate-line>
        {oldKey === newKey ? (
          <span className="text-xs text-status-waiting font-semibold">{t('Choisissez deux modèles différents.')}</span>
        ) : est.isLoading ? (
          <span className="text-xs text-adm-muted">{t('Estimation…')}</span>
        ) : est.error ? (
          <span className="text-xs text-status-stuck">{t('Estimation indisponible')} : {(est.error as Error).message}</span>
        ) : e ? (
          <span className="text-xs text-adm-ink-2">
            {t('Estimation')} : <b className={over ? 'text-status-waiting' : 'text-adm-ink'}>{fmtUsd(e.estimateUsd, 2)}</b>
            {' '}{t('pour {n} cas', { n: e.cases })}
            <span className="text-adm-muted"> · {t('par cas : ancien {o}, nouveau {w}', { o: fmtUsd(e.perCaseUsd.old, 4), w: fmtUsd(e.perCaseUsd.new, 4) })}</span>
            <span className="text-adm-muted"> · {t('plafond {c}', { c: fmtUsd(capUsd, 2) })}</span>
            {over && <span className="text-status-waiting font-semibold"> · {t('dépasse le plafond : la comparaison s’arrêtera avant la fin')}</span>}
          </span>
        ) : null}
        <Button type="submit" className="sm:ml-auto" loading={start.isPending} disabled={!ready || oldKey === newKey || !e}>{t('Lancer')}</Button>
      </div>
      {start.error && <p className="mt-2 text-xs text-status-stuck">{(start.error as Error).message}</p>}
    </form>
  );
}

function Chip({ children, tone = 'muted' }: { children: React.ReactNode; tone?: 'muted' | 'red' | 'gold' }) {
  const cls = tone === 'red' ? 'bg-status-stuck-bg text-status-stuck border-status-stuck-border'
    : tone === 'gold' ? 'bg-adm-gold-wash text-adm-gold-ink border-adm-hairline'
      : 'bg-adm-surface-3 text-adm-ink-2 border-adm-line';
  return <span className={`inline-block mr-1 mb-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cls}`}>{children}</span>;
}

// ─── runs list ───────────────────────────────────────────────────────────────

function RunsList({ selected, onPick }: { selected: string | null; onPick: (id: string) => void }) {
  const { t } = useT();
  const stateQ = useAiProvidersState();
  const anthropic = stateQ.data?.models.anthropic ?? [];
  const q = useQuery({
    queryKey: ['ai-compare-runs'], queryFn: aiProvidersApi.runs,
    refetchInterval: (query) => (query.state.data?.runs.some(runIsLive) ? 3000 : false),
  });
  const runs = q.data?.runs ?? [];
  return (
    <div className={card} data-runs>
      <h2 className="text-sm font-semibold text-adm-ink mb-2">{t('Comparaisons')}</h2>
      {q.isLoading && <p className="text-sm text-adm-muted">{t('Chargement…')}</p>}
      {!q.isLoading && runs.length === 0 && <p className="text-sm text-adm-muted">{t('Aucune comparaison pour l’instant.')}</p>}
      <ul className="divide-y divide-adm-line">
        {runs.map((r) => (
          <li key={r.id}>
            <button
              type="button" onClick={() => onPick(r.id)} data-run-row={r.id}
              className={`w-full text-left py-2.5 px-2 -mx-2 rounded-lg grid grid-cols-1 gap-y-0.5 min-h-11 ${selected === r.id ? 'bg-adm-gold-wash' : 'hover:bg-adm-surface-2'}`}
            >
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-adm-ink truncate">{t(aiFeatureLabel(r.task))}</span>
                <span className="block text-[11.5px] text-adm-muted truncate">
                  {hopLabel({ provider: r.old_provider, model: r.old_model }, anthropic, undefined, t)} → {hopLabel({ provider: r.new_provider, model: r.new_model }, anthropic, undefined, t)}
                </span>
              </span>
              <span className="text-[11.5px] text-adm-muted">
                <StatusChip status={r.status} /> · {r.done_count}/{r.case_count} · {fmtUsd(r.spent_usd, 2)} · {when(r.created_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── one run ─────────────────────────────────────────────────────────────────

function FacePhoto({ url, width, height, boxes, stroke, label }: { url: string; width: number | null; height: number | null; boxes: FaceBox[]; stroke: string; label: string }) {
  const { t } = useT();
  const [size, setSize] = useState<{ w: number; h: number } | null>(width && height ? { w: width, h: height } : null);
  useEffect(() => {
    if (size) return;
    const img = new Image();
    img.onload = () => setSize({ w: img.naturalWidth || 1000, h: img.naturalHeight || 750 });
    img.src = url;
  }, [url, size]);
  return (
    <figure className="min-w-0 m-0" data-face-photo={label}>
      <figcaption className="text-[11px] font-semibold text-adm-muted mb-1">{label} · {boxes.length > 1 ? t('{n} visages', { n: boxes.length }) : t('{n} visage', { n: boxes.length })}</figcaption>
      {size ? (
        <svg viewBox={`0 0 ${size.w} ${size.h}`} className="block w-full h-auto rounded-lg bg-adm-surface-3" role="img" aria-label={label} data-adm-media>
          <image href={url} x={0} y={0} width={size.w} height={size.h} preserveAspectRatio="none" />
          {boxes.map((b, i) => {
            const r = boxToRect(b, size.w, size.h);
            return (
              <g key={i}>
                <rect {...r} fill="none" stroke={stroke} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
                {b.label && <text x={r.x} y={Math.max(12, r.y - 4)} fill={stroke} fontSize={Math.max(12, size.w / 40)} fontWeight={700}>{b.label}</text>}
              </g>
            );
          })}
        </svg>
      ) : <div className="aspect-[4/3] rounded-lg bg-adm-surface-3 animate-pulse" />}
    </figure>
  );
}

function Side({ which, c }: { which: 'old' | 'new'; c: CompareCase }) {
  const { t } = useT();
  const out = which === 'old' ? c.old_output : c.new_output;
  const err = which === 'old' ? c.old_error : c.new_error;
  const cost = which === 'old' ? c.old_cost_usd : c.new_cost_usd;
  const ms = which === 'old' ? c.old_ms : c.new_ms;
  const model = which === 'old' ? c.old_model : c.new_model;
  const route = routeBadge(which === 'old' ? c.old_route : c.new_route);
  return (
    <div className="min-w-0 rounded-lg border border-adm-line bg-adm-surface-2 p-3" data-side={which}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
        <span className="text-[11px] font-bold uppercase tracking-wide text-adm-muted">{which === 'old' ? t('Ancien') : t('Nouveau')}</span>
        {route && <Chip tone="gold">{t(route)}</Chip>}
        <span className="text-[11px] text-adm-faint font-mono truncate min-w-0 flex-1" title={model ?? ''}>{model ?? ''}</span>
        <span className="text-[11px] tabular-nums text-adm-ink-2 whitespace-nowrap">{fmtUsd(cost, 4)} · {ms == null ? '—' : `${ms.toLocaleString('fr-FR')} ms`}</span>
      </div>
      {err ? <p className="text-[12.5px] text-status-stuck">{err}</p>
        : <p dir="auto" className="text-[12.5px] text-adm-ink whitespace-pre-wrap break-words">{out ?? '…'}</p>}
    </div>
  );
}

function CaseCard({ c, onVerdict, busy }: { c: CompareCase; onVerdict: (v: 'old' | 'new' | 'same') => void; busy: boolean }) {
  const { t } = useT();
  const face = c.task === 'face_finder' ? faceMeta(c.input_meta) : null;
  const photo = !face && c.input_meta && typeof c.input_meta.imageUrl === 'string' ? c.input_meta.imageUrl : null;
  const VERDICTS: Array<{ v: 'old' | 'new' | 'same'; label: string }> = [
    { v: 'old', label: t('ancien meilleur') }, { v: 'new', label: t('nouveau meilleur') }, { v: 'same', label: t('pareil') },
  ];
  return (
    <article className={`${cardBase} ${c.flagged ? 'border-status-stuck-border ring-1 ring-status-stuck-border' : 'border-adm-line'}`} data-case={c.id} data-flagged={c.flagged || undefined}>
      <div className="flex flex-wrap items-start gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-adm-faint font-mono">{c.case_ref}{c.status !== 'done' ? ` · ${c.status}` : ''}</p>
          {c.input_summary && <p dir="auto" className="text-[13px] text-adm-ink break-words">{c.input_summary}</p>}
          <div className="mt-1">{c.tags.map((tag) => <Chip key={tag}>{tagLabel(tag, t)}</Chip>)}</div>
        </div>
        {c.flagged && (
          <div className="basis-full sm:basis-auto sm:max-w-[45%] sm:text-right" data-flag>
            <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-status-stuck-solid text-white">⚠ {t('À vérifier')}</span>
            {c.flag_reasons.map((r) => <p key={r} className="text-[11px] text-status-stuck mt-0.5">{r}</p>)}
          </div>
        )}
      </div>
      {face && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-2.5">
          <FacePhoto url={face.imageUrl} width={face.width} height={face.height} boxes={face.oldBoxes} stroke={OLD_STROKE} label={t('Ancien')} />
          <FacePhoto url={face.imageUrl} width={face.width} height={face.height} boxes={face.newBoxes} stroke={NEW_STROKE} label={t('Nouveau')} />
        </div>
      )}
      {photo && <img src={photo} alt="" data-adm-media className="mb-2.5 max-h-48 rounded-lg object-contain" />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
        <Side which="old" c={c} />
        <Side which="new" c={c} />
      </div>
      <div className="mt-2.5 flex flex-wrap gap-1.5" role="group" aria-label={t('Verdict')}>
        {VERDICTS.map(({ v, label }) => (
          <button
            key={v} type="button" disabled={busy} aria-pressed={c.verdict === v} data-verdict={v}
            onClick={() => onVerdict(v)}
            className={`px-3 py-1.5 rounded-full border text-xs font-semibold min-h-11 md:min-h-0 disabled:opacity-60 ${c.verdict === v ? 'border-adm-gold bg-adm-gold-wash text-adm-gold-ink' : 'border-adm-line text-adm-muted hover:bg-adm-surface-2'}`}
          >
            {c.verdict === v ? '✓ ' : ''}{label}
          </button>
        ))}
      </div>
    </article>
  );
}

function RunView({ id }: { id: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const stateQ = useAiProvidersState();
  const anthropic = stateQ.data?.models.anthropic ?? [];
  const key = ['ai-compare-run', id] as const;
  const q = useQuery({
    queryKey: key, queryFn: () => aiProvidersApi.run(id),
    refetchInterval: (query) => (runIsLive(query.state.data?.run) ? 3000 : false),
  });
  const stop = useMutation({ mutationFn: () => aiProvidersApi.stopRun(id), onSuccess: () => qc.invalidateQueries({ queryKey: key }) });
  const verdict = useMutation({
    mutationFn: ({ caseId, v }: { caseId: string; v: CompareCase['verdict'] }) => aiProvidersApi.verdict(caseId, v),
    onMutate: ({ caseId, v }) => {
      qc.setQueryData(key, (d: { run: CompareRun; cases: CompareCase[] } | undefined) => d && { ...d, cases: d.cases.map((c) => (c.id === caseId ? { ...c, verdict: v } : c)) });
    },
    onError: () => qc.invalidateQueries({ queryKey: key }),
  });
  const cases = useMemo(() => q.data?.cases ?? [], [q.data?.cases]);
  const tot = useMemo(() => runTotals(cases), [cases]);
  if (q.isLoading) return <div className={card}><p className="text-sm text-adm-muted">{t('Chargement…')}</p></div>;
  if (q.error || !q.data) return <div className={card}><p className="text-sm text-status-stuck">{t('Comparaison introuvable.')}</p></div>;
  const r = q.data.run;
  const prog = runProgress(r);
  const live = runIsLive(r);
  const oldL = hopLabel({ provider: r.old_provider, model: r.old_model }, anthropic, undefined, t);
  const newL = hopLabel({ provider: r.new_provider, model: r.new_model }, anthropic, undefined, t);

  return (
    <section className="space-y-3" data-run={r.id}>
      <div className={card}>
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-semibold text-adm-ink">{t(aiFeatureLabel(r.task))} <StatusChip status={r.status} /></h2>
            <p className="text-xs text-adm-muted break-words"><b className="text-adm-ink-2">{t('Ancien')}</b> {oldL} · <b className="text-adm-ink-2">{t('Nouveau')}</b> {newL} · {when(r.created_at)}</p>
          </div>
          {live && <Button variant="secondary" size="sm" loading={stop.isPending} onClick={() => stop.mutate()}>{t('Arrêter')}</Button>}
        </div>
        {r.error && <p className="mt-2 text-xs text-status-stuck">{r.error}</p>}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-progress>
          <div className="space-y-1">
            <p className="text-[11.5px] text-adm-muted">{t('Cas traités')} : <b className="text-adm-ink">{r.done_count}/{r.case_count}</b></p>
            <Bar pct={prog.done} tone="ok" />
          </div>
          <div className="space-y-1">
            <p className="text-[11.5px] text-adm-muted">{t('Dépensé')} : <b className="text-adm-ink">{fmtUsd(r.spent_usd, 2)}</b> / {fmtUsd(r.cap_usd, 2)}
              {r.estimate_usd != null && <span> · {t('estimé {x}', { x: fmtUsd(r.estimate_usd, 2) })}</span>}</p>
            <Bar pct={prog.spent} tone={prog.spent >= 100 ? 'red' : prog.spent >= 80 ? 'amber' : 'ok'} />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2" data-totals>
          <Total label={t('Coût ancien')} value={fmtUsd(tot.oldUsd, 4)} />
          <Total label={t('Coût nouveau')} value={fmtUsd(tot.newUsd, 4)} />
          <Total label={t('Temps moyen')} value={`${tot.oldMs == null ? '—' : tot.oldMs.toLocaleString('fr-FR')} / ${tot.newMs == null ? '—' : tot.newMs.toLocaleString('fr-FR')} ms`} sub={t('ancien / nouveau')} />
          <Total label={t('Verdicts')} value={`${tot.verdicts.old} · ${tot.verdicts.new} · ${tot.verdicts.same}`} sub={t('ancien · nouveau · pareil')} />
        </div>
        {tot.flagged > 0 && <p className="mt-2 text-xs font-semibold text-status-stuck">⚠ {t('{n} cas à vérifier', { n: tot.flagged })}</p>}
      </div>
      {cases.map((c) => (
        <CaseCard key={c.id} c={c} busy={verdict.isPending && verdict.variables?.caseId === c.id}
          onVerdict={(v) => verdict.mutate({ caseId: c.id, v: nextVerdict(c.verdict, v) })} />
      ))}
      {cases.length === 0 && <div className={card}><p className="text-sm text-adm-muted">{live ? t('Les cas arrivent…') : t('Aucun cas.')}</p></div>}
    </section>
  );
}

function Total({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-adm-line bg-adm-surface-2 px-3 py-2 min-w-0">
      <div className="text-[11px] text-adm-muted font-medium">{label}</div>
      <div className="text-[15px] font-semibold text-adm-ink tabular-nums truncate">{value}</div>
      {sub && <div className="text-[10.5px] text-adm-faint">{sub}</div>}
    </div>
  );
}

export default function AdminAiComparePage() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();
  const runId = params.get('run');
  useEffect(() => { document.title = 'Comparer les modèles — Admin Storiz'; }, []);
  const pick = (id: string) => setParams((p) => { const n = new URLSearchParams(p); n.set('run', id); return n; });
  return (
    <AdminLayout title={t('Comparer les modèles')} subtitle={t('Les mêmes cas réels sur l’ancien et le nouveau modèle')}>
      <div className="max-w-6xl space-y-4">
        <p className="text-xs">
          <Link to="/admin/settings#set-ia-fournisseurs" className="font-semibold text-adm-gold-ink hover:underline">← {t('Fournisseurs IA')}</Link>
        </p>
        <NewRunForm onStarted={pick} />
        <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-4 items-start">
          <RunsList selected={runId} onPick={pick} />
          {runId ? <RunView key={runId} id={runId} /> : (
            <div className={card}><p className="text-sm text-adm-muted">{t('Choisissez une comparaison, ou lancez-en une.')}</p></div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
